import React, { forwardRef, useRef } from 'react'
import type { ButtonHTMLAttributes, MouseEvent, PointerEvent, TouchEvent } from 'react'
import {
    beginReliableTouchGesture,
    COMPATIBILITY_CLICK_SUPPRESSION_MS,
    RELIABLE_TOUCH_MOVE_THRESHOLD_PX,
    shouldActivateReliableTouchGesture,
    shouldSuppressCompatibilityClick,
    updateReliableTouchGesture,
} from './reliableTouchActivation'
import type { ReliableTouchGesture } from './reliableTouchActivation'

export type ReliableButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
    preserveFocusOnTouch?: boolean
}

function suppressTrailingTouchClick(
    button: HTMLButtonElement,
    {
        pointerId,
        clientX,
        clientY,
    }: Pick<globalThis.PointerEvent, 'pointerId' | 'clientX' | 'clientY'>,
) {
    const document = button.ownerDocument
    const expiresAt = Date.now() + COMPATIBILITY_CLICK_SUPPRESSION_MS
    const cleanup = () => {
        clearTimeout(timeout)
        document.removeEventListener('click', handleClick, true)
        document.removeEventListener('pointerdown', cleanup, true)
        document.removeEventListener('touchstart', cleanup, true)
        document.removeEventListener('keydown', cleanup, true)
    }
    const handleClick = (event: globalThis.MouseEvent) => {
        if (Date.now() >= expiresAt) {
            cleanup()
            return
        }
        if (!event.isTrusted) return

        const pointerClick = event as globalThis.PointerEvent
        if (pointerClick.pointerType) {
            if (pointerClick.pointerType !== 'touch' || pointerClick.pointerId !== pointerId) return
        } else if (
            event.detail === 0 ||
            Math.hypot(event.clientX - clientX, event.clientY - clientY) >
                RELIABLE_TOUCH_MOVE_THRESHOLD_PX
        ) {
            // Older browsers expose MouseEvent clicks; preserve their keyboard/AT detail=0 clicks.
            return
        }

        cleanup()
        event.preventDefault()
        event.stopImmediatePropagation()
    }
    const timeout = setTimeout(cleanup, COMPATIBILITY_CLICK_SUPPRESSION_MS)

    // A synchronous action can replace this button with a dialog before the native click arrives.
    // Keep this guard outside the component's lifetime, but release it for any fresh user input.
    document.addEventListener('click', handleClick, true)
    document.addEventListener('pointerdown', cleanup, true)
    document.addEventListener('touchstart', cleanup, true)
    document.addEventListener('keydown', cleanup, true)
}

/**
 * A native button that commits confirmed touch taps before a WebView can turn them into a
 * compatibility mouse sequence. Mouse, keyboard, and assistive-technology clicks stay native.
 */
const ReliableButton = forwardRef<HTMLButtonElement, ReliableButtonProps>(function ReliableButton(
    {
        disabled,
        onClick,
        onMouseDown,
        onPointerCancel,
        onPointerDown,
        onPointerMove,
        onPointerUp,
        onTouchCancel,
        onTouchEnd,
        onTouchMove,
        onTouchStart,
        preserveFocusOnTouch = false,
        type = 'button',
        ...props
    },
    ref,
) {
    const touchGestureRef = useRef<ReliableTouchGesture | null>(null)
    const pointerGestureRef = useRef<ReliableTouchGesture | null>(null)
    const suppressCompatibilityClickUntilRef = useRef(0)
    const dispatchingReliableClickRef = useRef(false)

    const handlePointerDown = (event: PointerEvent<HTMLButtonElement>) => {
        onPointerDown?.(event)

        if (!preserveFocusOnTouch || disabled || event.pointerType !== 'touch') return

        if (event.defaultPrevented) {
            pointerGestureRef.current = null
            return
        }

        if (pointerGestureRef.current) {
            pointerGestureRef.current.moved = true
            event.preventDefault()
            return
        }

        pointerGestureRef.current = beginReliableTouchGesture([
            {
                identifier: event.pointerId,
                clientX: event.clientX,
                clientY: event.clientY,
            },
        ])

        // React deliberately registers touchstart as passive, so focus preservation must use
        // cancellable pointerdown. Capture keeps the completion on this button when dismissing an
        // Android keyboard resizes the viewport or retargets the underlying touch sequence.
        event.preventDefault()
        try {
            event.currentTarget.setPointerCapture(event.pointerId)
        } catch {
            // If capture is unavailable, leave the subsequent touch events to the established
            // fallback path rather than trusting an uncaptured pointerup.
            pointerGestureRef.current = null
        }
    }

    const handlePointerMove = (event: PointerEvent<HTMLButtonElement>) => {
        onPointerMove?.(event)
        if (!preserveFocusOnTouch || event.pointerType !== 'touch') return

        pointerGestureRef.current = updateReliableTouchGesture(pointerGestureRef.current, [
            {
                identifier: event.pointerId,
                clientX: event.clientX,
                clientY: event.clientY,
            },
        ])
    }

    const releasePointerCapture = (event: PointerEvent<HTMLButtonElement>) => {
        try {
            if (event.currentTarget.hasPointerCapture(event.pointerId)) {
                event.currentTarget.releasePointerCapture(event.pointerId)
            }
        } catch {}
    }

    const handlePointerCancel = (event: PointerEvent<HTMLButtonElement>) => {
        onPointerCancel?.(event)
        if (!preserveFocusOnTouch || event.pointerType !== 'touch') return

        pointerGestureRef.current = null
        releasePointerCapture(event)
    }

    const handlePointerUp = (event: PointerEvent<HTMLButtonElement>) => {
        const button = event.currentTarget
        onPointerUp?.(event)
        if (!preserveFocusOnTouch || event.pointerType !== 'touch') return

        const shouldActivate = shouldActivateReliableTouchGesture(pointerGestureRef.current, [
            {
                identifier: event.pointerId,
                clientX: event.clientX,
                clientY: event.clientY,
            },
        ])
        const hadTrackedGesture = pointerGestureRef.current !== null
        pointerGestureRef.current = null
        releasePointerCapture(event)

        if (hadTrackedGesture) {
            suppressCompatibilityClickUntilRef.current =
                Date.now() + COMPATIBILITY_CLICK_SUPPRESSION_MS
        }

        if (!shouldActivate || disabled || event.defaultPrevented) return

        event.preventDefault()
        suppressTrailingTouchClick(button, event)
        dispatchingReliableClickRef.current = true
        try {
            button.click()
        } finally {
            dispatchingReliableClickRef.current = false
        }
    }

    const handleTouchStart = (event: TouchEvent<HTMLButtonElement>) => {
        onTouchStart?.(event)

        if (disabled || event.defaultPrevented) {
            pointerGestureRef.current = null
            touchGestureRef.current = null
            return
        }

        // Pointer-capable browsers use the captured pointer path above. Retain the touch path as
        // a fallback for older browsers that never emitted pointerdown.
        if (preserveFocusOnTouch && pointerGestureRef.current) {
            touchGestureRef.current = null
            return
        }

        touchGestureRef.current = beginReliableTouchGesture(event.touches)
    }

    const handleTouchMove = (event: TouchEvent<HTMLButtonElement>) => {
        onTouchMove?.(event)
        touchGestureRef.current = updateReliableTouchGesture(touchGestureRef.current, event.touches)
    }

    const handleTouchCancel = (event: TouchEvent<HTMLButtonElement>) => {
        touchGestureRef.current = null
        onTouchCancel?.(event)
    }

    const handleTouchEnd = (event: TouchEvent<HTMLButtonElement>) => {
        const button = event.currentTarget
        onTouchEnd?.(event)

        const shouldActivate = shouldActivateReliableTouchGesture(
            touchGestureRef.current,
            event.changedTouches,
            event.touches.length,
        )
        const hadTrackedGesture = touchGestureRef.current !== null
        touchGestureRef.current = null

        if (hadTrackedGesture) {
            suppressCompatibilityClickUntilRef.current =
                Date.now() + COMPATIBILITY_CLICK_SUPPRESSION_MS
        }

        if (!shouldActivate || disabled || event.defaultPrevented) return

        // A stationary touch is already a complete user gesture. Commit its native click now,
        // before the later compatibility mousedown can blur an editor, close a tray, reveal a
        // hover state, or synchronously unmount this button.
        event.preventDefault()
        dispatchingReliableClickRef.current = true
        try {
            button.click()
        } finally {
            dispatchingReliableClickRef.current = false
        }
    }

    const handleMouseDown = (event: MouseEvent<HTMLButtonElement>) => {
        onMouseDown?.(event)

        if (event.detail !== 0 && Date.now() < suppressCompatibilityClickUntilRef.current) {
            event.preventDefault()
        }
    }

    const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
        if (
            shouldSuppressCompatibilityClick({
                clickDetail: event.detail,
                dispatchingReliableClick: dispatchingReliableClickRef.current,
                now: Date.now(),
                suppressUntil: suppressCompatibilityClickUntilRef.current,
            })
        ) {
            event.preventDefault()
            event.stopPropagation()
            return
        }

        onClick?.(event)
    }

    return (
        <button
            {...props}
            ref={ref}
            type={type}
            disabled={disabled}
            onClick={handleClick}
            onMouseDown={handleMouseDown}
            onPointerCancel={handlePointerCancel}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onTouchCancel={handleTouchCancel}
        />
    )
})

export default ReliableButton
